import { zodResolver } from "@hookform/resolvers/zod";
import React from 'react';
import { useForm } from "react-hook-form";
import * as z from "zod";
import { inputTypeMap, type InputTypeMap, type InterventionInput } from '../../types';
import { Button } from "../ui/button";
import { Card } from '../ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

interface AddEditInterventionInputProps {
  onSubmit: (values: Partial<InterventionInput>) => void;
  onCancel: () => void;
  initialValues?: InterventionInput | null;
}

// Define form schema with input_code
const formSchema = z.object({
  input_code: z.string().min(1, "Input code is required"),
  input_name: z.string().min(1, "Input name is required"),
  input_type: z.enum(  Object.keys(inputTypeMap) as [keyof InputTypeMap, ...(keyof InputTypeMap)[]]),
  emission_factor: z.coerce.number().min(0, "Must be a positive number"),
  emission_factor_unit: z.string().min(1, "Unit is required"),
  input_source: z.string().min(1, "Source is required"),
  year: z.string().min(1, "Year is required"),
  region: z.string().min(1, "Region is required"),
  version: z.string().optional(),
  status: z.string().min(1, "Status is required"),
  
  // Type-specific fields
  fertilizer_type: z.string().optional(),
  nitrogen_content: z.coerce.number().optional(),
  crop: z.string().optional(),
  
  // Nitrogen stabilized fertilizer fields
  stabilizer_type: z.string().optional(),
  
  // Controlled release fertilizer fields
  crf_blend_percent: z.coerce.number().optional(),
  
  // Fuel fields
  fuel_type: z.string().optional(),
});

const AddEditInterventionInput: React.FC<AddEditInterventionInputProps> = ({
  onSubmit,
  onCancel,
  initialValues
}) => {
  // Define form with react-hook-form
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues || {
      input_code: "",
      input_name: "",
      input_type: "conventional_fertilizer",
      emission_factor: 0,
      emission_factor_unit: "",
      input_source: "",
      year: new Date().getFullYear().toString(),
      region: "",
      version: "1.0",
      status: "active",
    }
  });

  // Set form values when initialValues change
  React.useEffect(() => {
    if (initialValues) {
      form.reset(initialValues);
    }
  }, [initialValues, form]);

  // Get the selected input type
  const inputType = form.watch("input_type");

  // Handle form submission
  const handleSubmit = (values: z.infer<typeof formSchema>) => {
    onSubmit(values);
  };

  // Render type-specific fields based on input type
  const renderTypeSpecificFields = () => {
    switch (inputType) {
      case 'conventional_fertilizer':
        return (
          <Card className="p-4 mt-4">
            <h3 className="text-lg font-medium mb-4">Fertilizer Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="fertilizer_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fertilizer Type</FormLabel>
                    <FormControl>
                      <Input placeholder="Urea, Ammonium Nitrate, etc." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="nitrogen_content"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nitrogen Content (%)</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="%" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="crop"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Crop (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., Corn, Wheat, etc." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </Card>
        );
      
      case 'low_carbon_fertilizer':
        return (
          <Card className="p-4 mt-4">
            <h3 className="text-lg font-medium mb-4">Low Carbon Fertilizer Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="fertilizer_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fertilizer Type</FormLabel>
                    <FormControl>
                      <Input placeholder="Urea, Ammonium Nitrate, etc." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="nitrogen_content"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nitrogen Content (%)</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="%" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </Card>
        );

      case 'nitrogen_stabilized_fertilizer':
        return (
          <Card className="p-4 mt-4">
            <h3 className="text-lg font-medium mb-4">Nitrogen Stabilized Fertilizer Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="fertilizer_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fertilizer Type</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., Urea, CAN, etc." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="nitrogen_content"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nitrogen Content (%)</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="%" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="stabilizer_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Stabilizer Type</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., DMPP, NBPT, etc." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="crop"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Crop (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., Corn, Wheat, etc." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </Card>
        );

      case 'controlled_release_fertilizer':
        return (
          <Card className="p-4 mt-4">
            <h3 className="text-lg font-medium mb-4">Controlled Release Fertilizer Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="fertilizer_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fertilizer Type</FormLabel>
                    <FormControl>
                      <Input placeholder="Urea, Ammonium Nitrate, etc." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="nitrogen_content"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nitrogen Content (%)</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="%" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="crf_blend_percent"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CRF Blend Percent (%)</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="%" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="crop"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Crop (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., Corn, Wheat, etc." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </Card>
        );
      
      case 'fuel':
        return (
          <Card className="p-4 mt-4">
            <h3 className="text-lg font-medium mb-4">Fuel Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="fuel_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fuel Type</FormLabel>
                    <FormControl>
                      <Input placeholder="Diesel, Gasoline, etc." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </Card>
        );
      
      default:
        return null;
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="input_code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Input Code</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., F001" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="input_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Input Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter input name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="input_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Input Type</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select input type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="conventional_fertilizer">Fertilizer (Conventional)</SelectItem>
                    <SelectItem value="nitrogen_stabilized_fertilizer">Nitrogen Stabilized Fertilizer</SelectItem>
                    <SelectItem value="controlled_release_fertilizer">Controlled Release Fertilizer</SelectItem>
                    <SelectItem value="low_carbon_fertilizer">Low Carbon Fertilizer</SelectItem>
                    <SelectItem value="fuel">Fuel</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="emission_factor"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Emission Factor</FormLabel>
                <FormControl>
                  <Input type="number" step="0.0001" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="emission_factor_unit"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Unit</FormLabel>
                <FormControl>
                  <Input placeholder="kg CO2e/kg N" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="input_source"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Source</FormLabel>
                <FormControl>
                  <Input placeholder="IPCC, EPA, etc." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="year"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Year</FormLabel>
                <FormControl>
                  <Input placeholder="2023" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="region"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Region</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., EU, US, etc." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="version"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Version</FormLabel>
                <FormControl>
                  <Input placeholder="1.0" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {renderTypeSpecificFields()}

        <div className="flex justify-end space-x-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit">
            {initialValues ? "Update" : "Create"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default AddEditInterventionInput;